import _ from 'underscore';
import { storeSeasonalityAdjustmentName } from '@bingads-webui-campaign/seasonality-id-name-utils';

const editorialRejectionReasonFilter = (params, i18n) => {
  let flagIds = [];
  let formatDate = null;

  if (_.isString(params)) {
    const items = params.split(';');

    if (items.length > 1) {
      flagIds = _.map(items[0].split(','), item => parseInt(item, 10));
      formatDate = items[1] && i18n.parseDate(items[1], { raw: 'yyyy-MM-dd' });
    } else if (items.length === 1 && params.match('\\d{4}-\\d{2}-\\d{2}')) {
      // only filter for date
      formatDate = i18n.parseDate(params, { raw: 'yyyy-MM-dd' });
    } else {
      flagIds = _.map(items[0].split(','), item => parseInt(item, 10));
    }
  } else if (_.isFinite(params)) {
    flagIds = [parseInt(params, 10)];
  }

  const filters = [...(flagIds.length > 0 ? [{
    SelectedColumn: 'EditorialRejectionFlagId',
    SelectedOperator: 'EnumSelection',
    Values: flagIds,
  }] : []),
  ...(formatDate ? [{
    SelectedColumn: 'EditorialRejectionDate',
    SelectedOperator: 'GreaterOrEqual',
    Values: [formatDate],
  }] : [])];

  return filters.length ? filters : null;
};

export const featureIdToFilterAlias = {
  AdGrid: {
    filterDisapprovedAds: [{
      SelectedColumn: 'EditorialStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.EditorialStatus'Rejected'"],
    }, {
      SelectedColumn: 'IsAppealPending',
      SelectedOperator: 'Equal',
      Values: ['false'],
    }],
    filterAdResourceInvalidAds: [{
      SelectedColumn: 'AdResourceStatusId',
      SelectedOperator: 'Equal',
      Values: ["Enum.AdResourceStatus'Invalid'"],
    }],
    filterGoogleImportedVideoNeedRepair: [{
      SelectedColumn: 'AdResourceStatusId',
      SelectedOperator: 'Equal',
      Values: ["Enum.AdResourceStatus'GoogleImportedVideoNeedRepair'"],
    }],
    filterDisapprovedOrApprovedLimitedAds: [{
      SelectedColumn: 'DeliveryStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.DeliveryStatus'EditorialRejected'", "Enum.DeliveryStatus'EditorialApprovedLimited'"],
    }],
    editorialRejectionReason: editorialRejectionReasonFilter,
    filterHeadlinesAssetId: paramString =>
      [{
        SelectedColumn: 'HeadlinesAssetId',
        SelectedOperator: 'Contains',
        Values: [paramString],
      }],
    filterDescriptionsAssetId: paramString =>
      [{
        SelectedColumn: 'DescriptionsAssetId',
        SelectedOperator: 'Contains',
        Values: [paramString],
      }],
  },
  KeywordGrid: {
    filterDisapprovedKeywords: [{
      SelectedColumn: 'EditorialStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.EditorialStatus'Rejected'"],
    }, {
      SelectedColumn: 'IsAppealPending',
      SelectedOperator: 'Equal',
      Values: ['false'],
    }],
    filterDisapprovedOrApprovedLimitedKeywords: [{
      SelectedColumn: 'DeliveryStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.DeliveryStatus'EditorialRejected'", "Enum.DeliveryStatus'EditorialApprovedLimited'"],
    }],
    editorialRejectionReason: editorialRejectionReasonFilter,
  },
  CampaignsGrid: {
    budgetCappedCampaigns: [{
      SelectedColumn: 'DeliveryStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.DeliveryStatus'BudgetSuggestions'"],
    }],
    newCampaignBudgetSuggestion: [{
      SelectedColumn: 'DeliveryStatus',
      SelectedOperator: 'EnumSelection',
      Values: [
        "Enum.DeliveryStatus'CampaignBudgetPaused'",
        "Enum.DeliveryStatus'BudgetSuggestions'",
      ],
    }],
    newCampaign01: campaignName => ([{
      SelectedColumn: 'CampaignName',
      SelectedOperator: 'Contains',
      Values: [campaignName],
    }]),
    filterBidStrategy: (value, i18n, permissions) => {
      const isInPilot = _.chain(permissions).result('dynamic').result('PortfolioBidStrategy', false).value();
      const bidStrategyName = `${value}`; // Convert to string
      const isValid = !_.isEmpty(bidStrategyName);

      return isInPilot && isValid ? [{
        SelectedColumn: 'BidStrategyName',
        SelectedOperator: 'Equal',
        Values: [bidStrategyName],
      }] : [];
    },
    filterSeasonalityCampaignType: (campaignType) => {
      const campaignTypeList = campaignType.split(',');
      return [{
        SelectedColumn: 'CampaignType',
        SelectedOperator: 'EnumSelection',
        Values: campaignTypeList,
      }];
    },
    filterSeasonalityId: (options) => {
      const re = /^(\d+), (.*)/;
      const matches = re.exec(options);
      const id = parseInt(matches[1], 10);
      const name = matches[2];

      storeSeasonalityAdjustmentName({ id, name });
      return [{
        SelectedColumn: 'SeasonalityAdjustmentId',
        SelectedOperator: 'Equal',
        Values: [id],
      }];
    },
    filterDataExclusionId: (options) => {
      const re = /^(\d+), (.*)/;
      const matches = re.exec(options);
      const id = parseInt(matches[1], 10);
      const name = matches[2];

      storeSeasonalityAdjustmentName({ id, name });
      return [{
        SelectedColumn: 'DataExclusionId',
        SelectedOperator: 'Equal',
        Values: [id],
      }];
    },
  },
  AudienceAssociationGrid: {
    hcRem01: audienceId => ([{
      SelectedColumn: 'AudienceStatus',
      SelectedOperator: 'EnumSelection',
      Values: ['161', '165'],
    },
    ...audienceId === '' ? [] : [{
      SelectedColumn: 'AudienceId',
      SelectedOperator: 'Equal',
      Values: [audienceId],
    }],
    ]),
    hcRem02: audienceId => ([{
      SelectedColumn: 'AudienceStatus',
      SelectedOperator: 'EnumSelection',
      Values: ['161'],
    },
    {
      SelectedColumn: 'AudienceCriterionBidMultiplier',
      SelectedOperator: 'Equal',
      Values: [0],
    },
    ...audienceId === '' ? [] : [{
      SelectedColumn: 'AudienceId',
      SelectedOperator: 'Equal',
      Values: [audienceId],
    }],
    ]),
    alertsCanobidadj01: [{
      SelectedColumn: 'AudienceCriterionBidMultiplier',
      SelectedOperator: 'Equal',
      Values: [0],
    }],
    alertsCainactive01: [{
      SelectedColumn: 'AudienceStatus',
      SelectedOperator: 'EnumSelection',
      Values: ['162'],
    }],
    alertsCanobidadj02: [{
      SelectedColumn: 'AudienceStatus',
      SelectedOperator: 'EnumSelection',
      Values: ['161'],
    }, {
      SelectedColumn: 'AudienceCriterionBidMultiplier',
      SelectedOperator: 'Equal',
      Values: [0],
    }],
  },
  AudienceGrid: {
    newAssociation01: associationId => ([{
      SelectedColumn: 'Id',
      SelectedOperator: 'Equal',
      Values: [associationId],
    }]),
  },
  UETTagsGrid: {
    alertsUetinactive01: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Unverified'", "Enum.TagTrackingStatus'Inactive'"],
    }, {
      SelectedColumn: 'AudiencesCount',
      SelectedOperator: 'Greater',
      Values: [0],
    }],
    alertsUetinactive02: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Unverified'", "Enum.TagTrackingStatus'Inactive'"],
    }, {
      SelectedColumn: 'GoalsCount',
      SelectedOperator: 'Greater',
      Values: [0],
    }],
    alertsUetinactive03: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Unverified'", "Enum.TagTrackingStatus'Inactive'"],
    }, {
      SelectedColumn: 'AudiencesCount',
      SelectedOperator: 'Equal',
      Values: [0],
    }, {
      SelectedColumn: 'GoalsCount',
      SelectedOperator: 'Equal',
      Values: [0],
    }],
    recommendationUetinactive: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Inactive'"],
    }, {
      SelectedColumn: 'GoalsCount',
      SelectedOperator: 'Greater',
      Values: [0],
    }],
    alertsUetactive01: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Active'"],
    }, {
      SelectedColumn: 'AudiencesCount',
      SelectedOperator: 'Equal',
      Values: [0],
    }],
    alertsUetactive02: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Active'"],
    }, {
      SelectedColumn: 'GoalsCount',
      SelectedOperator: 'Equal',
      Values: [0],
    }],
    alertsCgnorecent01: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'NoRecentConversion'"],
    }],
    alertsSanoassociation01: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.TagTrackingStatus'Active'"],
    }, {
      SelectedColumn: 'AssociationsCount',
      SelectedOperator: 'Equal',
      Values: [0],
    }],
  },
  ConversionGoalsGrid: {
    unverifiedGoals: [{
      SelectedColumn: 'TrackingStatus',
      SelectedOperator: 'EnumSelection',
      Values: ["Enum.GoalTrackingStatus'Unverified'"],
    }],
  },
};
